import React, { useState, useEffect } from "react";
import { MdAttachMoney, MdApi, MdSupportAgent, MdQueryStats, MdModelTraining, MdDateRange } from "react-icons/md";

const BillingUsage = () => {
  const [billingData, setBillingData] = useState([]);
  const [currentPlan, setCurrentPlan] = useState("Freemium");
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Available package plans
  const packagePlans = ["Freemium", "Starter Pack", "Elite", "Enterprise Grade"];

  // Mock billing data with -focused items
  const fetchBillingData = (from, to) => {
    const mockData = [
      {
        itemName: " Input Tokens",
        volume: 120000,
        cost: 12.00,
        description: "Tokens processed for user queries to  models",
        icon: <MdQueryStats className="text-indigo-600 text-2xl" />,
      },
      {
        itemName: " Output Tokens",
        volume: 60000,
        cost: 9.00,
        description: "Tokens generated in responses by  models",
        icon: <MdQueryStats className="text-indigo-600 text-2xl" />,
      },
      {
        itemName: "Knowledge Base Queries",
        volume: 500,
        cost: 25.00,
        description: "Searches against your custom  knowledge base",
        icon: <MdApi className="text-indigo-600 text-2xl" />,
      },
      {
        itemName: "Priority Support Hours",
        volume: 5,
        cost: 50.00,
        description: "Dedicated support hours for priority assistance",
        icon: <MdSupportAgent className="text-indigo-600 text-2xl" />,
      },
      {
        itemName: "Custom Model Training",
        volume: 2,
        cost: 200.00,
        description: "Compute hours for fine-tuning  models",
        icon: <MdModelTraining className="text-indigo-600 text-2xl" />,
      },
      {
        itemName: "API Call Credits",
        volume: 1000,
        cost: 15.00,
        description: "Credits for API calls to  services",
        icon: <MdApi className="text-indigo-600 text-2xl" />,
      },
    ];
    setBillingData(mockData);
  };

  // Mock invoice data (last 6 months from August 2025)
  const generateMockInvoices = () => {
    const invoices = [];
    const currentDate = new Date(2025, 7); // August 2025 (month is 0-indexed)
    for (let i = 0; i < 6; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      invoices.push(`${year}-${month}-Invoice.txt`);
    }
    return invoices;
  };

  // Handle invoice download
  const handleDownloadInvoice = (filename) => {
    const content = `Invoice for ${filename}\nTotal: $${totalCost}\nItems:\n${billingData
      .map((item) => `${item.itemName}: ${item.volume} units, $${item.cost}`)
      .join("\n")}`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Calculate total cost
  const totalCost = billingData.reduce((sum, item) => sum + item.cost, 0).toFixed(2);

  // Fetch default billing data on component mount (current period)
  useEffect(() => {
    fetchBillingData(); // Load active bill without specific dates
  }, []);

  // Handle package selection
  const handlePlanChange = (plan) => {
    setCurrentPlan(plan);
    setIsPlanModalOpen(false);
  };

  // Handle filter duration submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    if (dateFrom && dateTo) {
      // Validate dates
      if (new Date(dateTo) < new Date(dateFrom)) {
        alert("End date must be after start date.");
        return;
      }
      fetchBillingData(dateFrom, dateTo); // Replace with actual API call
      setIsFilterModalOpen(false);
    } else {
      alert("Please select both start and end dates.");
    }
  };

  return (
    <div className="container mx-auto p-6 bg-gray-50 min-h-screen">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Billing & Usage</h2>

      {/* Package Plan and Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Package Plan Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 flex justify-between items-center transform transition-all duration-300 hover:shadow-xl">
          <div>
            <h3 className="text-lg font-semibold text-gray-700">Current Package Plan</h3>
            <p className="text-2xl font-bold text-indigo-600">{currentPlan}</p>
          </div>
          <button
            onClick={() => setIsPlanModalOpen(true)}
            className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors"
          >
            Change Package
          </button>
        </div>

        {/* Summary Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 flex justify-between items-center transform transition-all duration-300 hover:shadow-xl">
          <div className="flex items-center">
            <MdAttachMoney className="text-indigo-600 text-4xl mr-4" />
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Total Billing</h3>
              <p className="text-2xl font-bold text-gray-800">${totalCost}</p>
              <p className="text-sm text-gray-500">
                {dateFrom && dateTo
                  ? `From ${new Date(dateFrom).toLocaleDateString()} to ${new Date(dateTo).toLocaleDateString()}`
                  : "Current Billing Period"}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-2">
            <button
              onClick={() => setIsFilterModalOpen(true)}
              className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors"
            >
              Filter Duration
            </button>
            <button
              onClick={() => setIsInvoicesModalOpen(true)}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              Past Invoices
            </button>
          </div>
        </div>
      </div>

      {/* Billing Data Cards */}
      {billingData.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {billingData.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg p-6 transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
            >
              <div className="flex items-center mb-4">
                {item.icon}
                <h4 className="ml-3 text-lg font-semibold text-gray-800">{item.itemName}</h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">{item.description}</p>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">Volume</p>
                  <p className="text-lg font-medium text-gray-800">{item.volume.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Cost</p>
                  <p className="text-lg font-medium text-gray-800">${item.cost.toFixed(2)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-500 py-8">
          No billing data available for the selected period.
        </div>
      )}

      {/* Change Package Modal */}
      {isPlanModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Package Plan</h3>
            <div className="space-y-2">
              {packagePlans.map((plan) => (
                <button
                  key={plan}
                  onClick={() => handlePlanChange(plan)}
                  className={`w-full text-left px-4 py-2 rounded-md text-sm font-medium ${
                    currentPlan === plan
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  } transition-colors`}
                >
                  {plan}
                </button>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setIsPlanModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Duration Modal */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Billing Duration</h3>
            <form onSubmit={handleFilterSubmit} className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Date From</label>
                  <div className="relative mt-1">
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                    <MdDateRange className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700">Date To</label>
                  <div className="relative mt-1">
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      required
                    />
                    <MdDateRange className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsFilterModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                >
                  Apply Filter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Past Invoices Modal */}
      {isInvoicesModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[60vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Past Invoices</h3>
            <div className="space-y-2">
              {generateMockInvoices().map((invoice, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span className="text-sm text-gray-700">{invoice}</span>
                  <button
                    onClick={() => handleDownloadInvoice(invoice)}
                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setIsInvoicesModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingUsage;